// src/pages/dataInput/Step3DGConfig.tsx
import React from "react";
import { cls } from "../theme/theme";
import { DGAppConfig, DGConfigModal } from "../dg/DGConfigModal";
import { SourcePayload } from "./types";
import { Step2Values } from "./Step2Metadata";

interface Props {
  source:    SourcePayload;
  metadata:  Step2Values;
  dgConfig:  DGAppConfig | null;
  onChange:  (cfg: DGAppConfig | null) => void;
}

export const Step3DGConfig: React.FC<Props> = ({
  source, metadata, dgConfig, onChange,
}) => {
  const [showModal, setShowModal] = React.useState(false);

  const summary = React.useMemo(() => {
    if (!dgConfig) return null;
    const enabledGroups = Object.values(dgConfig.groups).filter(g => g.enabled).length;
    const totalAttrs    = Object.values(dgConfig.groups).reduce(
      (s, g) => s + Object.values(g.attrs).filter((a: any) => a.enabled).length, 0
    );
    const customised    = Object.values(dgConfig.groups).reduce(
      (s, g) => s + Object.values(g.lovCustomized || {}).filter(Boolean).length, 0
    );
    return { enabledGroups, totalAttrs, customised };
  }, [dgConfig]);

  // Source contributes table count
  const tableCount = (() => {
    switch (source.kind) {
      case "file":    return (source.sheetConfigs ?? []).filter(s => s.included).length;
      case "ddl":     return source.parsedTables?.length ?? 0;
      case "db":      return source.dbSource?.tables?.length ?? 0;
      case "catalog": return source.catalogTables?.length ?? 0;
    }
  })();

  return (
    <div>
      <div className="gw-section-hdr">Data governance enrichment</div>

      {/* DG config card */}
      <div style={{
        padding: 18, borderRadius: 12, border: "1.5px solid #c8d4e0",
        background: "#dde7f5", display: "flex", alignItems: "center",
        gap: 14, marginBottom: 18,
      }}>
        <span style={{ fontSize: 28 }}>⚙️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e3a6e" }}>
            Governance enrichment configuration
          </div>
          <div style={{ fontSize: 12.5, color: "#1e3a6e", marginTop: 4 }}>
            {summary
              ? <>
                  <strong>{summary.enabledGroups}</strong> groups · {" "}
                  <strong>{summary.totalAttrs}</strong> attributes
                  {summary.customised > 0 && <> · <strong>{summary.customised}</strong> customised</>}
                </>
              : "Click Configure to load and customise enrichment settings."
            }
          </div>
        </div>
        <button
          className="gw-btn gw-btn-primary"
          onClick={() => setShowModal(true)}
        >
          {dgConfig ? "Edit configuration" : "Configure →"}
        </button>
      </div>

      <DGConfigModal
        config={dgConfig}
        onApply={onChange}
        show={showModal}
        onHide={() => setShowModal(false)}
        app_nm={metadata.appName || undefined}
        entity={metadata.entity || undefined}
      />

      {/* Final review */}
      <div className="gw-section-hdr" style={{ marginTop: 28 }}>Review</div>
      <div style={{
        padding: "0", borderRadius: 12, border: "1px solid #c8d4e0",
        background: "#fff", overflow: "hidden",
      }}>
        <div className="gw-card-hdr">
          <span className="gw-card-title">Pipeline summary</span>
          <span className="gw-mode-badge raw">{source.sheetMode === "sub_entities" ? "sub-entities" : "separate entities"}</span>
        </div>
        <div style={{ padding: "14px 16px", fontSize: 13, color: "#1e2d3d" }}>
          <ReviewRow label="Source" value={source.label} />
          <ReviewRow label="Tables / Sheets" value={`${tableCount}`} />
          <ReviewRow label="Application" value={metadata.appName || "—"} />
          <ReviewRow label="Source system" value={metadata.systemName || "—"} />
          <ReviewRow label="Domain" value={metadata.domain || "—"} />
          {source.sheetMode === "sub_entities" && (
            <ReviewRow label="Parent entity" value={metadata.entity || "—"} />
          )}
          <ReviewRow
            label="Relationships"
            value={metadata.relationships.length > 0
              ? `${metadata.relationships.length} defined`
              : "none"}
          />
          <ReviewRow
            label="DG enrichment"
            value={summary
              ? `${summary.enabledGroups} groups · ${summary.totalAttrs} attributes`
              : "default"}
          />
        </div>
      </div>
    </div>
  );
};

const ReviewRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", padding: "6px 0",
    borderBottom: "1px solid #f1f5f9", gap: 12,
  }}>
    <span style={{
      fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5,
      color: "#445566", letterSpacing: ".06em", textTransform: "uppercase",
    }}>{label}</span>
    <span style={{ color: "#0f1923", fontWeight: 500, textAlign: "right" }}>{value}</span>
  </div>
);

export default Step3DGConfig;